import { useTheme } from '@mui/material/styles';

import { Chart } from './Chart';
import { baseChartOptions, categoryAxis, useChartScheme, valueAxis } from './chartTheme';

export interface CategoryBarChartProps {
  readonly ariaLabel: string;
  readonly categories: readonly string[];
  /** Display strings for the table alternative, one per value. */
  readonly labels: readonly string[];
  readonly rowHeader: string;
  readonly tableCaption: string;
  readonly valueHeader: string;
  readonly values: readonly number[];
  /**
   * Formats a plotted number for the axis ticks and the tooltip. Required: ECharts would otherwise print
   * the bare float, so an amount would reach the screen without ever passing through `formatMoney`.
   */
  readonly formatValue: (value: number) => string;
  /** See `ChartProps.tableProvidedByCaller`. */
  readonly tableProvidedByCaller?: boolean;
}

export function CategoryBarChart({
  ariaLabel,
  categories,
  formatValue,
  labels,
  rowHeader,
  tableCaption,
  tableProvidedByCaller,
  valueHeader,
  values,
}: CategoryBarChartProps) {
  const theme = useTheme();
  const scheme = useChartScheme();
  const base = baseChartOptions(theme, scheme);
  // ECharts draws a category axis bottom-up, so the plotted order is the reverse of the ranking order.
  // Reversing rows into one array — rather than reversing categories, values, and labels separately —
  // keeps a bar, its axis label, and its tooltip text provably aligned to the same row.
  const plotted = categories
    .map((category, index) => ({
      category,
      label: labels[index] ?? '',
      value: values[index] ?? 0,
    }))
    .reverse();

  return (
    <Chart
      ariaLabel={ariaLabel}
      columns={[{ header: valueHeader, values: labels }]}
      height={Math.max(180, categories.length * 44)}
      option={{
        ...base,
        // One series: the card title names the measure, so a one-swatch legend would be noise.
        legend: { show: false },
        series: [
          {
            barMaxWidth: 24,
            data: plotted.map((row) => row.value),
            // 4px rounded data-end, square at the baseline.
            itemStyle: { borderRadius: [0, 4, 4, 0] },
            type: 'bar' as const,
          },
        ],
        tooltip: {
          ...base.tooltip,
          // Without a formatter the tooltip prints the raw plotted number. `label` is the same formatted
          // string the accessible table shows, so both surfaces read identically.
          formatter: (params: { dataIndex: number }) => {
            const row = plotted[params.dataIndex];
            return row === undefined ? '' : `${row.category}<br/>${row.label}`;
          },
          trigger: 'item' as const,
        },
        // Horizontal bars: long game and affiliate names get room to read.
        xAxis: valueAxis(theme, scheme, formatValue),
        yAxis: { ...categoryAxis(theme, scheme), data: plotted.map((row) => row.category) },
      }}
      rowHeader={rowHeader}
      rows={categories}
      tableCaption={tableCaption}
      {...(tableProvidedByCaller === undefined ? {} : { tableProvidedByCaller })}
    />
  );
}
